/**
 * Citation Text Parser (Simplified for Normalized Schema)
 *
 * Parses summary text with pre-structured citation segments from database
 * and returns React elements with inline CitationPill components.
 */

import React, { ReactNode } from 'react';
import CitationPill from '../components/papers/CitationPill';
import { parseSummaryText, renderSummaryTextRange } from './summaryText';
import type { CitationSegment } from '../components/papers/types';

interface PillPosition {
    y: number;
    height: number;
}

/**
 * Parse text with citations and return array of text fragments and CitationPill components
 *
 * @param text - The summary text to parse
 * @param segments - Array of citation segments (already includes start/end indices from DB)
 * @param onHoverChange - Optional callback for when any pill hover state changes
 * @param style - Optional text style to apply to LaTeX segments
 * @param fontsLoaded - Whether custom fonts are loaded
 * @returns Array of React nodes (text strings and CitationPill components)
 */
export function parseTextWithCitations(
    text: string,
    segments: CitationSegment[] | undefined,
    onHoverChange?: (isHovered: boolean, tooltipText?: string, position?: PillPosition) => void,
    style?: any,
    fontsLoaded?: boolean
): ReactNode[] {
    const textSegments = parseSummaryText(text);
    const renderRange = (start: number, end: number, key: string) =>
        renderSummaryTextRange(textSegments, start, end, key, style, fontsLoaded);

    // Apply the same emphasis and LaTeX formatting with or without citations.
    if (!segments || segments.length === 0 || !text) {
        return renderRange(0, text.length, 'text-plain');
    }

    // Sort segments by start index to process them in order
    const sortedSegments = [...segments].sort(
        (a, b) => (a.segment_start ?? 0) - (b.segment_start ?? 0)
    );

    const elements: ReactNode[] = [];
    let lastIndex = 0;
    let pillCounter = 0;

    sortedSegments.forEach((segment) => {
        const startIndex = segment.segment_start ?? 0;
        const endIndex = segment.segment_end ?? text.length;

        // Add text before this segment (if any)
        if (startIndex > lastIndex) {
            elements.push(...renderRange(lastIndex, startIndex, `text-before-${pillCounter}`));
        }

        // Add the segment text
        elements.push(...renderRange(startIndex, endIndex, `text-segment-${pillCounter}`));

        // Add a pill for EACH chunk in this segment
        if (segment.chunks && segment.chunks.length > 0) {
            segment.chunks.forEach((chunk) => {
                pillCounter++;
                elements.push(
                    <CitationPill
                        key={`pill-${pillCounter}`}
                        pillNumber={pillCounter}
                        chunkText={chunk.chunk_text}
                        onHoverChange={onHoverChange}
                    />
                );
            });
        }

        lastIndex = endIndex;
    });

    // Add remaining text after the last segment
    if (lastIndex < text.length) {
        elements.push(...renderRange(lastIndex, text.length, 'text-remaining'));
    }

    return elements;
}

/**
 * Parse text with citations and return text elements and pills separately
 * This is useful when you want to render pills at the end instead of inline
 *
 * @param text - The summary text to parse
 * @param segments - Array of citation segments (already includes start/end indices from DB)
 * @param onHoverChange - Optional callback for when any pill hover state changes
 * @param style - Optional text style to apply to LaTeX segments
 * @param fontsLoaded - Whether custom fonts are loaded
 * @returns Object with textElements and pills arrays
 */
export function parseTextWithCitationsAtEnd(
    text: string,
    segments: CitationSegment[] | undefined,
    onHoverChange?: (isHovered: boolean, tooltipText?: string, position?: PillPosition) => void,
    style?: any,
    fontsLoaded?: boolean
): { textElements: ReactNode[], pills: ReactNode[] } {
    // Pills are rendered after the text: source offsets have no display role.
    // They may be UTF-8 offsets into the pre-normalized LLM response, so slicing
    // the stored description with them can split math or duplicate overlapping text.
    const textElements = parseTextWithCitations(text, undefined, undefined, style, fontsLoaded);
    const pills: ReactNode[] = [];
    const seenChunkTexts = new Set<string>();
    const sortedSegments = [...(segments ?? [])].sort(
        (a, b) => (a.segment_start ?? 0) - (b.segment_start ?? 0)
    );

    sortedSegments.forEach((segment) => {
        (segment.chunks ?? []).forEach((chunk) => {
            const normalizedText = chunk.chunk_text.trim();
            if (seenChunkTexts.has(normalizedText)) return;
            seenChunkTexts.add(normalizedText);
            const pillNumber = pills.length + 1;
            pills.push(
                <CitationPill
                    key={`pill-${pillNumber}`}
                    pillNumber={pillNumber}
                    chunkText={chunk.chunk_text}
                    onHoverChange={onHoverChange}
                />
            );
        });
    });

    return { textElements, pills };
}
